// Mob spawning (MOB-4). PURE (ENG-1).
//
// Per spawn cycle, for each category under its cap, a few attempts pick a point
// in the ring [MIN_RADIUS, MAX_RADIUS] around a player, validate the light/ground
// conditions, and spawn a small pack. Monsters need darkness (block light ≤ 0,
// and night on the surface); passive creatures need light ≥ 9 on grass. All
// randomness flows through the injected Rng (ENG-3), so a seeded run is stable.
import { MOB, TICK } from '../config';
import type { MobDef } from '../registry/schemas';
import type { Difficulty, EntitySystem, MobCategory, MobWorld } from './entity';
import { isSlimeChunk } from '../game/slime_chunk';

export interface SpawnContext {
  world: MobWorld;
  dayTime: number;
  difficulty: Difficulty;
  /** Active dimension — nether mobs spawn only in the nether (MOB-4). */
  dimension: 'overworld' | 'nether' | 'end';
  /** World seed — gates slime chunks (MB.6). */
  seed: number | string | bigint;
  doMobSpawning: boolean;
  players: readonly { x: number; y: number; z: number }[];
  /** Standable feet-Y at column (x,z), or null if unloaded / no surface. */
  surfaceY(x: number, z: number): number | null;
  /** Block name directly under a feet position (grass check), or null. */
  groundName(x: number, y: number, z: number): string | null;
  /** Surface biome at a column. Absent ⇒ biome gating is off and every mob is
   *  eligible everywhere (the behaviour before biome-gated spawning). */
  biomeName?(x: number, z: number): string;
  /** Is (x,z) inside the footprint of a structure of this type? Absent ⇒
   *  structure-gated mobs (the elder guardian) simply never spawn. */
  inStructure?(type: string, x: number, z: number): boolean;
}

/** Where a mob is placed: standing in an air pocket, or submerged in water. */
export type SpawnMedium = 'land' | 'water';

/** A category's default placement, when no def is in hand. */
function mediumOfCategory(category: MobCategory): SpawnMedium {
  return category === 'water_creature' || category === 'water_ambient' ? 'water' : 'land';
}

/** A mob's placement: its category's default, unless it declares itself aquatic. */
export function spawnMediumOf(def: MobDef): SpawnMedium {
  return def.spawn.aquatic ? 'water' : mediumOfCategory(def.spawn.category);
}

/** True when the world time-of-day is in the hostile-spawn night window. */
export function isNight(dayTime: number): boolean {
  const t = dayTime % TICK.DAY_TICKS;
  return t >= 13_000 && t < 23_000;
}

/** True if (x,y,z) is a submerged 2-high water pocket — where a squid/fish spawns. */
function submerged(world: SpawnContext['world'], x: number, y: number, z: number): boolean {
  return world.isFluid(x, y, z) && world.isFluid(x, y + 1, z);
}

/**
 * MOB-4 condition predicate for one candidate feet position. `def` refines the
 * category defaults when the caller has already picked a mob: its placement medium
 * (an aquatic monster is submerged, not stood on a floor) and its own ground list.
 */
export function canSpawnAt(
  category: MobCategory, ctx: SpawnContext, x: number, y: number, z: number, def?: MobDef,
): boolean {
  const { world } = ctx;
  // Aquatic mobs (squid and fish by category, guardians by their own flag) spawn
  // submerged, not on a floor.
  if ((def ? spawnMediumOf(def) : mediumOfCategory(category)) === 'water') return submerged(world, x, y, z);
  // Need a 2-high air gap for the body.
  if (world.isSolid(x, y, z) || world.isSolid(x, y + 1, z)) return false;
  // …and something to stand on. An explicit ground list replaces the solid-floor
  // test entirely, which is what lets a strider spawn on the lava sea.
  const ground = def?.spawn.ground ?? [];
  if (ground.length > 0) {
    const below = ctx.groundName(x, y - 1, z);
    if (below === null || !ground.includes(below)) return false;
  } else if (!world.isSolid(x, y - 1, z)) return false;
  const blockLight = world.getBlockLight(x, y, z);
  const skyLight = world.getSkyLight(x, y, z);
  if (category === 'monster') {
    // Nether mobs ignore light entirely (as in Minecraft) — the dimension is lit by
    // lava and glowstone, so a light gate would confine them to a few dark corners.
    if (ctx.dimension === 'nether') return true;
    if (blockLight > MOB.SPAWN.MONSTER_LIGHT_MAX) return false;
    // Surface (sky-lit) columns only spawn hostiles at night; caves any time.
    return skyLight <= 0 || isNight(ctx.dayTime);
  }
  if (category === 'ambient') {
    // Bats: dark, and (like hostiles) only at night on the surface / any time in a cave.
    if (blockLight > MOB.SPAWN.AMBIENT_LIGHT_MAX) return false;
    return skyLight <= 0 || isNight(ctx.dayTime);
  }
  // A creature that named its own ground carries its own rules with it — there is
  // no daylight in the nether for a hoglin to wait for.
  if (ground.length > 0) return true;
  // Passive creatures: bright, on grass.
  if (Math.max(blockLight, skyLight) < MOB.SPAWN.CREATURE_LIGHT_MIN) return false;
  return ctx.groundName(x, y - 1, z) === 'grass_block';
}

/**
 * Weighted pick of a mob in `category` (+ dimension, + biome) from the registry.
 *
 * `spawn.biomes` has always been in the mob schema but was never read: every mob
 * was equally likely anywhere its category could spawn. A non-empty list now means
 * "only here" — which is what finally puts cod and salmon in the ocean instead of
 * in a village pond, and keeps a mob restricted to one biome out of every other.
 * An empty list still means "anywhere", and passing no biome disables the gate
 * entirely, so callers that don't know the biome behave exactly as before.
 */
export function pickSpawnMob(
  defs: ReadonlyMap<string, MobDef>,
  category: MobCategory,
  roll: number,
  dimension: 'overworld' | 'nether' | 'end' = 'overworld',
  biome?: string,
  /** Structure predicate for structure-gated mobs. Absent ⇒ none are eligible. */
  inStructure?: (type: string) => boolean,
): MobDef | null {
  let total = 0;
  const pool: MobDef[] = [];
  for (const d of defs.values()) {
    if (d.spawn.dimension !== dimension && d.spawn.dimension !== 'any') continue;
    if (biome !== undefined && d.spawn.biomes.length > 0 && !d.spawn.biomes.includes(biome)) continue;
    // An elder guardian belongs to its monument, not to the open ocean.
    if (d.spawn.structure !== undefined && !inStructure?.(d.spawn.structure)) continue;
    if (d.category === category || d.spawn.category === category) { pool.push(d); total += d.spawn.weight; }
  }
  if (total === 0) return null;
  let r = roll * total;
  for (const d of pool) { r -= d.spawn.weight; if (r < 0) return d; }
  return pool[pool.length - 1] ?? null;
}

const SPAWN_CATEGORIES: readonly MobCategory[] = ['monster', 'creature', 'ambient', 'water_creature'];

/** Feet-Y for a spawn candidate: the standable surface on land, or (aquatic) the
 *  topmost submerged cell within a window below `nearY` — where a squid drifts. */
function spawnFeetY(
  category: MobCategory, ctx: SpawnContext, x: number, z: number, nearY: number, def?: MobDef,
): number | null {
  if ((def ? spawnMediumOf(def) : mediumOfCategory(category)) === 'land') return ctx.surfaceY(x, z);
  const top = Math.floor(nearY) + 8;
  for (let y = top; y > top - 40; y--) if (submerged(ctx.world, x, y, z)) return y;
  return null;
}

/** Run one spawn cycle (MOB-4). Returns the number of mobs spawned. */
export function trySpawn(sys: EntitySystem, ctx: SpawnContext): number {
  if (!ctx.doMobSpawning || ctx.players.length === 0) return 0;
  let spawned = 0;
  for (const category of SPAWN_CATEGORIES) {
    if (category === 'monster' && ctx.difficulty === 'peaceful') continue;
    const cap = MOB.CAPS[category];
    if (sys.countCategory(category) >= cap) continue;

    for (let attempt = 0; attempt < MOB.SPAWN.ATTEMPTS; attempt++) {
      if (sys.countCategory(category) >= cap) break;
      const player = ctx.players[sys.rng.nextInt(ctx.players.length)]!;
      const angle = sys.rng.nextFloat() * Math.PI * 2;
      const radius = MOB.SPAWN.MIN_RADIUS + sys.rng.nextFloat() * (MOB.SPAWN.MAX_RADIUS - MOB.SPAWN.MIN_RADIUS);
      const cx = Math.floor(player.x + Math.cos(angle) * radius);
      const cz = Math.floor(player.z + Math.sin(angle) * radius);
      // The mob is picked BEFORE the placement is validated, because the placement
      // now depends on the mob: an aquatic monster is looked for underwater and a
      // strider on the lava, neither of which its category alone would find.
      const def = pickSpawnMob(
        sys.defs, category, sys.rng.nextFloat(), ctx.dimension,
        ctx.biomeName?.(cx, cz), (t) => ctx.inStructure?.(t, cx, cz) ?? false,
      );
      if (!def) continue;
      const feetY = spawnFeetY(category, ctx, cx, cz, player.y, def);
      if (feetY === null) continue;
      if (!canSpawnAt(category, ctx, cx, feetY, cz, def)) continue;
      // Slimes only spawn in slime chunks (MB.6 natural spawn weights).
      if (def.name === 'slime' && !isSlimeChunk(Math.floor(cx / 16), Math.floor(cz / 16), ctx.seed)) continue;

      const packMax = Math.min(def.spawn.packMax, cap - sys.countCategory(category));
      const pack = def.spawn.packMin + sys.rng.nextInt(Math.max(1, packMax - def.spawn.packMin + 1));
      for (let i = 0; i < pack; i++) {
        if (sys.countCategory(category) >= cap) break;
        const ox = cx + sys.rng.nextInt(MOB.SPAWN.PACK_SPREAD * 2 + 1) - MOB.SPAWN.PACK_SPREAD;
        const oz = cz + sys.rng.nextInt(MOB.SPAWN.PACK_SPREAD * 2 + 1) - MOB.SPAWN.PACK_SPREAD;
        const oy = spawnFeetY(category, ctx, ox, oz, feetY, def);
        if (oy === null || !canSpawnAt(category, ctx, ox, oy, oz, def)) continue;
        if (sys.spawn(def.name, ox + 0.5, oy, oz + 0.5)) spawned++;
      }
    }
  }
  return spawned;
}
